import { defineStore } from 'pinia'
import { readCookie } from '@/modules/cookies'
import { buildAssessmentPayload } from '../utils/assessmentPayload'
import type { AssessmentPayloadEntry } from '../utils/assessmentPayload'
import { recordedSymptoms } from '../utils/symptoms'
import type {
  GuidedAgeUnit,
  GuidedAssessmentPath,
  GuidedAnswer,
  GuidedAnswerAttributes,
  GuidedConsultationPath,
  GuidedMaternalStatus,
  GuidedModeHandoff,
  GuidedModeConfig,
  GuidedModeDefinition,
  GuidedModeObservation,
  GuidedModeSymptom,
  GuidedModeTrack,
  GuidedModeView,
  GuidedPatientSex,
} from '../types/guided-mode.types'

export function toConversionObservations(observations: GuidedModeObservation[]) {
  return observations.map(observation => ({
    type: 'response' as const,
    name: observation.name,
    value: String(observation.value),
  }))
}

export interface GuidedModeAssumption {
  conceptId?: string
  code?: string
  hadPrevious: boolean
  previous?: GuidedAnswer
  overridden?: true
}

export interface GuidedAnswerEscalation {
  answers: Record<string, GuidedAnswer>
  progressFloor: number
}

export interface GuidedFocusedPage {
  id: string
  sourceStepKey: string
  sourceGroupId?: string
  leafIds: string[]
  kind: 'group' | 'question'
  titleToken?: string
  multiselect?: boolean
}

const initialProgress = () => ({
  view: 'demographics' as GuidedModeView,
  currentIndex: 0,
  answers: {} as Record<string, GuidedAnswer>,
  answerAttributes: {} as Record<string, GuidedAnswerAttributes>,
  activeAssessmentPath: null as GuidedAssessmentPath | null,
  requestedAssessmentPath: null as GuidedAssessmentPath | null,
  age: undefined as number | undefined,
  ageUnit: 'years' as GuidedAgeUnit,
  sex: '' as GuidedPatientSex,
  maternalStatus: '' as GuidedMaternalStatus,
  identifierValue: '',
  selectedSymptomIds: [] as string[],
  recordedSymptomIds: [] as string[],
  otherSymptomSelected: false,
  comprehensiveSelected: false,
  focusedPanelNames: [] as string[],
  focusedSelections: [] as string[],
  triggeredSymptomIds: [] as string[],
  focusedStepIds: null as string[] | null,
  progressBase: null as number | null,
  progressBaseAnswered: 0,
  progressFloor: 0,
  confirmedConceptIds: [] as string[],
  visitedStepKeys: [] as string[],
  forwardStepKeys: [] as string[],
  seenStepIds: [] as string[],
  pendingFocusedStepIds: [] as string[],
  focusedResumeStepKey: null as string | null,
  introducedFocusedStepIds: [] as string[],
  introducedFocusedStepOrigins: {} as Record<string, string[]>,
  focusedBaselineStepIds: null as string[] | null,
  focusedBaselineSymptomIds: [] as string[],
  focusedBaselineSelections: null as string[] | null,
  focusedActiveOwners: [] as string[],
  focusedPresentedPages: [] as GuidedFocusedPage[],
  focusedCurrentPageId: null as string | null,
  committedAssumeSymptomIds: [] as string[],
  committedAskSymptomIds: [] as string[],
  confirmedAskSymptomIds: [] as string[],
  recordedEntryPointSymptomIds: [] as string[],
  assumedSymptomAnswers: {} as Record<string, GuidedModeAssumption>,
  answerEscalation: null as GuidedAnswerEscalation | null,
  keptEscalationStepIds: [] as string[],
  // Once the worker has seen the Assessment Summary, a change made from it returns there.
  summaryReached: false,
})

export type GuidedModeProgress = ReturnType<typeof initialProgress>

type DynamicFocusedProgress = Pick<
  GuidedModeProgress,
  | 'pendingFocusedStepIds'
  | 'focusedResumeStepKey'
  | 'introducedFocusedStepIds'
  | 'introducedFocusedStepOrigins'
  | 'committedAssumeSymptomIds'
  | 'committedAskSymptomIds'
  | 'confirmedAskSymptomIds'
  | 'recordedEntryPointSymptomIds'
  | 'assumedSymptomAnswers'
  | 'focusedBaselineStepIds'
  | 'focusedBaselineSymptomIds'
  | 'focusedBaselineSelections'
  | 'focusedActiveOwners'
  | 'focusedPresentedPages'
  | 'focusedCurrentPageId'
>

export type GuidedModeSnapshot = Omit<
  GuidedModeProgress,
  | 'answerAttributes'
  | 'requestedAssessmentPath'
  | 'focusedPanelNames'
  | 'focusedSelections'
  | 'progressBase'
  | 'progressBaseAnswered'
  | 'progressFloor'
  | 'confirmedConceptIds'
  | 'visitedStepKeys'
  | 'forwardStepKeys'
  | 'seenStepIds'
  | 'answerEscalation'
  | 'keptEscalationStepIds'
  | 'summaryReached'
  | keyof DynamicFocusedProgress
> & {
  answerAttributes?: Record<string, GuidedAnswerAttributes>
  requestedAssessmentPath?: GuidedAssessmentPath | null
  focusedPanelNames?: string[]
  focusedSelections?: string[]
  progressBase?: number | null
  progressBaseAnswered?: number
  progressFloor?: number
  confirmedConceptIds?: string[]
  visitedStepKeys?: string[]
  forwardStepKeys?: string[]
  seenStepIds?: string[]
  answerEscalation?: GuidedAnswerEscalation | null
  keptEscalationStepIds?: string[]
  summaryReached?: boolean
  observations: GuidedModeObservation[]
} & Partial<DynamicFocusedProgress>

export const useGuidedModeStore = defineStore('guidedMode', {
  state: () => ({
    clinicalContent: { assessmentDefinitions: {} as Record<string, GuidedModeDefinition> } as GuidedModeConfig,
    tracks: [] as GuidedModeTrack[],
    observations: [] as GuidedModeObservation[],
    engagementKey: null as string | null,
    ...initialProgress(),
  }),

  getters: {
    fixedAnswers: (state): Record<string, GuidedAnswer> => {
      const definition = state.activeAssessmentPath
        ? state.clinicalContent.assessmentDefinitions?.[state.activeAssessmentPath] : undefined
      if (!definition) return {}
      return Object.fromEntries((definition.fixedObservations || []).flatMap(observation => {
        const concept = Object.entries(definition.concepts)
          .find(([, value]) => value.code?.code === observation.name)
        if (!concept) return []
        const value = observation.type === 'number' && observation.value === 'Unknown'
          ? 'unknown' : observation.value
        return [[concept[0], value]]
      }))
    },
    effectiveAnswers(): Record<string, GuidedAnswer> {
      return { ...this.answers, ...this.fixedAnswers }
    },
    observationsForConversion: (state) => toConversionObservations(state.observations),
    startedFocusedAssessments(): GuidedModeSymptom[] {
      return recordedSymptoms(
        this.activeAssessmentPath ? this.clinicalContent.symptoms?.[this.activeAssessmentPath] || [] : [],
        this.recordedSymptomIds,
        this.effectiveAnswers,
      )
    },
    consultationPath(): GuidedConsultationPath | null {
      if (!this.activeAssessmentPath) return null
      if (this.comprehensiveSelected || this.otherSymptomSelected) return 'comprehensive'
      return this.startedFocusedAssessments.length ? 'focused' : null
    },
    // Every question of the track, answered or not, each carrying the skip flag the
    // questionnaire derives from its `visibleWhen` rules. `assess()` reads this to tell a
    // question the skip logic hid from one the user simply has not reached yet.
    assessmentPayload(): AssessmentPayloadEntry[] {
      const definition = this.activeAssessmentPath
        ? this.clinicalContent.assessmentDefinitions?.[this.activeAssessmentPath] : undefined
      return buildAssessmentPayload(definition, this.effectiveAnswers, {
        excludedDataPointCodes: this.clinicalContent.questionEligibility?.[this.activeAssessmentPath!] || [],
      })
    },
  },

  actions: {
    setAnswers(answers: Record<string, GuidedAnswer>) {
      // Fixed values belong to the definition, never to editable/persisted responses.
      this.answers = Object.fromEntries(Object.entries(answers)
        .filter(([conceptId]) => !Object.hasOwn(this.fixedAnswers, conceptId)))
    },
    setAnswerAttributes(conceptId: string, attributes: GuidedAnswerAttributes) {
      const next = { ...this.answerAttributes }
      const hasValue = Object.values(attributes).some(value => value !== undefined)
      if (hasValue) next[conceptId] = { ...attributes }
      else delete next[conceptId]
      this.answerAttributes = next
    },
    setConfig(config: { clinicalContent?: GuidedModeConfig; tracks?: GuidedModeTrack[] }) {
      this.clinicalContent = config.clinicalContent || { assessmentDefinitions: {} }
      this.tracks = config.tracks || []
    },
    setObservations(observations: GuidedModeObservation[]) {
      this.observations = observations
    },
    clearObservations() {
      this.observations = []
    },
    setFocusedAssessmentMetadata(panelNames: string[], selections: string[]) {
      this.focusedPanelNames = [...panelNames]
      this.focusedSelections = [...selections]
    },
    clearFocusedPanels() {
      this.focusedPanelNames = []
    },
    rebaseProgress(base: number, answered: number) {
      this.progressBase = base
      this.progressBaseAnswered = answered
      this.progressFloor = Math.max(this.progressFloor, base)
    },
    restartProgress(base: number, answered: number) {
      this.progressBase = base
      this.progressBaseAnswered = answered
      this.progressFloor = base
    },
    clearProgress() {
      this.progressBase = null
      this.progressBaseAnswered = 0
      this.progressFloor = 0
    },
    resume(payload: GuidedModeSnapshot): void {
      Object.assign(this, { ...initialProgress(), ...payload })
    },
    resetProgress(): void {
      Object.assign(this, initialProgress())
    },

    endSession(): void {
      this.resetProgress()
      this.clearObservations()
    },
    prepareComprehensiveHandoff(handoff: GuidedModeHandoff): void {
      this.resetProgress()
      this.clearObservations()
      this.requestedAssessmentPath = handoff.path
      this.age = handoff.age
      this.ageUnit = handoff.ageUnit || 'years'
      this.sex = handoff.sex || ''
      this.maternalStatus = handoff.maternalStatus || ''
      this.identifierValue = handoff.identifierValue || ''
    },
    clearRequestedAssessmentPath(): void {
      this.requestedAssessmentPath = null
    },

    syncEngagement(): void {
      const key = readCookie('session')
      if (this.engagementKey === key) return

      if (this.engagementKey === null) {
        this.engagementKey = key
        return
      }

      this.$reset()
      this.$patch({ engagementKey: key })
    },
  },

  persist: {
    pick: ['clinicalContent', 'tracks', 'engagementKey'],
  },
})
